import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import ProductCodeBadge from '../components/ProductCodeBadge';
import MovementCalendar from '../components/movements/MovementCalendar';
import {
  ChevronLeft, ChevronRight, Calendar, Truck, Package, X, Plus, Send, Eye,
  CheckCircle2, Clock, MapPin, FileText, Link2, Loader2, ArrowRight, Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const STATUS_STYLES = {
  'Planned':    'bg-zinc-800 text-zinc-300 border-zinc-700',
  'Packed':     'bg-blue-900/40 text-blue-300 border-blue-700',
  'In Transit': 'bg-amber-900/40 text-amber-300 border-amber-700',
  'Delivered':  'bg-green-900/40 text-green-300 border-green-700',
  'Received':   'bg-green-900/60 text-green-200 border-green-600',
};

export default function DispatchCalendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [dispatches, setDispatches] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedDispatch, setExpandedDispatch] = useState(null);
  const [tab, setTab] = useState('movements');

  // Form data
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    client: '', sales_order_id: '', dispatch_date: today.toISOString().slice(0, 10),
    same_day: true, dispatch_mode: 'transport', transporter_name: '', vehicle_no: '',
    lr_no: '', tracking_link: '', total_weight: '', notes: '', gst_invoice: false,
    invoice_no: '', eway_bill_no: '', expected_delivery: '',
    items: [{ product_id: '', product_name: '', qty: '', unit: 'kg' }],
  });
  const [showAddTransporter, setShowAddTransporter] = useState(false);
  const [newTransporter, setNewTransporter] = useState({ name: '', type: 'transport', contact: '', phone: '' });

  // Define fetchData before useAdminDelete to avoid TDZ error
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, oRes, fRes, tRes] = await Promise.all([
        axios.get(`${API}/dispatch/calendar/view`, { withCredentials: true }),
        axios.get(`${API}/sales/orders`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/inventory/finished-goods`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/dispatch/transporters`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setDispatches(dRes.data.timeline || []);
      setMetrics(dRes.data.metrics || {});
      setSalesOrders(oRes.data || []);
      setProducts(fRes.data || []);
      setTransporters(tRes.data || []);
      const uniqueClients = [...new Set((oRes.data || []).map(o => o.client).filter(Boolean))];
      setClients(uniqueClients);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const { deleteState, requestDelete, confirmDelete, cancelDelete } = useAdminDelete(fetchData);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Calendar
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const dispatchMap = useMemo(() => {
    const m = {};
    dispatches.forEach(d => { m[d.date] = d.dispatches; });
    return m;
  }, [dispatches]);

  const allDispatches = useMemo(() => dispatches.flatMap(d => d.dispatches), [dispatches]);

  const calCells = [];
  for (let i = 0; i < firstDay; i++) calCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calCells.push(d);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: form.items.filter(it => it.product_id && it.qty),
        total_weight: parseFloat(form.total_weight) || 0,
      };
      // Auto-fill from sales order if selected
      if (form.sales_order_id) {
        const so = salesOrders.find(o => o.id === form.sales_order_id);
        if (so) {
          payload.client = payload.client || so.client;
          payload.voucher_no = so.voucher_no || '';
          if (!payload.items.length || !payload.items[0].product_id) {
            payload.items = (so.items || []).map(it => ({
              product_id: it.product_id || '', product_name: it.product_name || '',
              qty: it.qty || 0, unit: it.unit || 'kg',
            }));
          }
        }
      }
      await axios.post(`${API}/dispatch/create`, payload, { withCredentials: true });
      toast.success('Dispatch created!');
      setShowCreate(false);
      setForm({
        client: '', sales_order_id: '', dispatch_date: today.toISOString().slice(0, 10),
        same_day: true, dispatch_mode: 'transport', transporter_name: '', vehicle_no: '',
        lr_no: '', tracking_link: '', total_weight: '', notes: '', gst_invoice: false,
        invoice_no: '', eway_bill_no: '', expected_delivery: '',
        items: [{ product_id: '', product_name: '', qty: '', unit: 'kg' }],
      });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/dispatch/${id}/status`, { status }, { withCredentials: true });
      toast.success(`Dispatch → ${status}`);
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const shareTracking = async (id) => {
    await axios.patch(`${API}/dispatch/${id}/share-tracking`, {}, { withCredentials: true });
    toast.success('Tracking shared with client');
    fetchData();
  };

  // deleteDispatch now uses useAdminDelete hook via requestDelete (see button onClick handlers)

  const addTransporter = async () => {
    try {
      const res = await axios.post(`${API}/dispatch/transporters`, newTransporter, { withCredentials: true });
      setTransporters(prev => [...prev, res.data]);
      setForm(f => ({ ...f, transporter_name: newTransporter.name }));
      setShowAddTransporter(false);
      setNewTransporter({ name: '', type: 'transport', contact: '', phone: '' });
      toast.success('Transporter added');
    } catch (e) { toast.error('Failed'); }
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { product_id: '', product_name: '', qty: '', unit: 'kg' }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, val) => setForm(f => {
    const items = [...f.items];
    items[i] = { ...items[i], [field]: val };
    if (field === 'product_id') {
      const p = products.find(x => x.id === val);
      if (p) items[i].product_name = p.name;
    }
    return { ...f, items };
  });

  const selectedDispatches = selectedDate ? (dispatchMap[selectedDate] || []) : [];

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse">Loading dispatches...</div>;

  return (
    <div className="space-y-4" data-testid="dispatch-calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2">
            <Truck size={22} className="text-green-400" /> Dispatch & Outward
          </h1>
          <p className="text-zinc-500 text-sm mt-0.5">{metrics.total || 0} dispatches / {metrics.in_transit || 0} in transit / {metrics.delivered || 0} delivered</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="create-dispatch-btn"
          className="flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white font-semibold text-xs px-3 py-2 rounded-xl">
          <Plus size={14} /> New Dispatch
        </button>
      </div>

      {/* Status Cards */}
      {tab !== 'movements' && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
        {[
          { label: 'Planned', count: metrics.planned || 0, cls: 'border-zinc-700 text-zinc-300' },
          { label: 'In Transit', count: metrics.in_transit || 0, cls: 'border-amber-700 text-amber-300' },
          { label: 'Delivered', count: metrics.delivered || 0, cls: 'border-green-700 text-green-300' },
          { label: 'Total Weight', count: `${(metrics.total_weight_kg || 0).toLocaleString()} kg`, cls: 'border-zinc-700 text-zinc-400' },
        ].map(c => (
          <div key={c.label} className={`bg-zinc-900 border rounded-xl p-3 text-center ${c.cls}`}>
            <div className="text-xl font-heading font-bold">{c.count}</div>
            <div className="text-xs mt-0.5 opacity-80">{c.label}</div>
          </div>
        ))}
      </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 overflow-x-auto max-w-full">
        {[['movements', 'Outward Calendar', Send], ['calendar', 'Dispatch Cards', Calendar], ['table', 'All Dispatches', FileText]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`dsp-tab-${k}`}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${tab === k ? 'bg-green-600 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}>
            <Icon size={13} />{l}
          </button>
        ))}
      </div>

      {/* ══ OUTWARD MOVEMENTS (bills = dispatch) ══ */}
      {tab === 'movements' && <MovementCalendar direction="outward" />}

      {/* ══ CALENDAR ══ */}
      {tab === 'calendar' && (
        <>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 bg-zinc-800/50">
              <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))} className="text-zinc-400 hover:text-zinc-200 p-1"><ChevronLeft size={18} /></button>
              <span className="text-zinc-100 font-heading font-semibold">{MONTH_NAMES[month]} {year}</span>
              <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))} className="text-zinc-400 hover:text-zinc-200 p-1"><ChevronRight size={18} /></button>
            </div>
            <div className="grid grid-cols-7">
              {DAY_NAMES.map(d => <div key={d} className="text-center text-zinc-500 text-xs font-medium py-2 border-b border-zinc-800">{d}</div>)}
              {calCells.map((day, i) => {
                if (!day) return <div key={`e-${i}`} className="min-h-[70px] border-b border-r border-zinc-800/40" />;
                const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                const dayDisp = dispatchMap[dateStr] || [];
                const isToday = dateStr === today.toISOString().slice(0, 10);
                const isSel = selectedDate === dateStr;
                return (
                  <div key={day} onClick={() => setSelectedDate(isSel ? null : dateStr)}
                    className={`min-h-[70px] border-b border-r border-zinc-800/40 px-1.5 py-1 cursor-pointer transition-colors ${isSel ? 'bg-green-900/20' : isToday ? 'bg-amber-900/10' : 'hover:bg-zinc-800/30'}`}>
                    <div className={`text-xs font-mono ${isToday ? 'text-amber-400 font-bold' : 'text-zinc-500'}`}>{day}</div>
                    {dayDisp.length > 0 && (
                      <div className="mt-0.5 space-y-0.5">
                        {dayDisp.slice(0, 2).map((d, j) => (
                          <div key={j} className={`text-[8px] rounded px-1 py-0 truncate border ${STATUS_STYLES[d.status] || 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                            {d.client?.slice(0, 12)}
                          </div>
                        ))}
                        {dayDisp.length > 2 && <div className="text-[8px] text-zinc-600">+{dayDisp.length - 2}</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected date detail */}
          {selectedDate && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-green-400" />
                  <span className="text-zinc-100 text-sm font-semibold font-mono">{selectedDate}</span>
                  <span className="text-zinc-500 text-xs">{selectedDispatches.length} dispatch(es)</span>
                </div>
                <button onClick={() => setSelectedDate(null)} className="text-zinc-500 hover:text-zinc-300"><X size={16} /></button>
              </div>
              {selectedDispatches.length > 0 ? (
                <DispatchTable dispatches={selectedDispatches} onStatusChange={updateStatus} onShare={shareTracking}
                  expanded={expandedDispatch} onExpand={setExpandedDispatch} isAdmin={isAdmin} onDelete={(id, client) => requestDelete(`/dispatch/${id}`, id, `Delete dispatch ${id} for ${client}. FG stock restored, sales order unlinked.`)} />
              ) : (
                <div className="p-4 text-zinc-600 text-xs">No dispatches on this date. <button onClick={() => { setForm(f => ({ ...f, dispatch_date: selectedDate })); setShowCreate(true); }} className="text-green-400 underline">Create one</button></div>
              )}
            </div>
          )}
        </>
      )}

      {/* ══ TABLE VIEW ══ */}
      {tab === 'table' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <DispatchTable dispatches={allDispatches} onStatusChange={updateStatus} onShare={shareTracking}
            expanded={expandedDispatch} onExpand={setExpandedDispatch} isAdmin={isAdmin} onDelete={(id, client) => requestDelete(`/dispatch/${id}`, id, `Delete dispatch ${id} for ${client}. FG stock restored.`)} />
          {allDispatches.length === 0 && <div className="text-center text-zinc-500 text-sm py-8">No dispatches yet</div>}
        </div>
      )}

      {/* ══ CREATE MODAL ══ */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50 flex items-center gap-2"><Truck size={16} className="text-green-400" /> Create Dispatch</h3>
              <button onClick={() => setShowCreate(false)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4 max-h-[70vh] overflow-y-auto" data-testid="dispatch-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Client *</label>
                  <select required value={form.client} onChange={e => setForm(f => ({ ...f, client: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-green-500">
                    <option value="">Select client...</option>
                    {clients.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Link to Sales Order</label>
                  <select value={form.sales_order_id} onChange={e => {
                    const so = salesOrders.find(o => o.id === e.target.value);
                    setForm(f => ({ ...f, sales_order_id: e.target.value, client: so?.client || f.client,
                      gst_invoice: so?.gst_enabled || false, invoice_no: so?.voucher_no || '' }));
                  }}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-green-500">
                    <option value="">None (manual)</option>
                    {salesOrders.filter(o => o.status !== 'Dispatched').map(o => (
                      <option key={o.id} value={o.id}>{o.voucher_no || o.id.slice(0,8)} — {o.client} ({o.status})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-zinc-400 text-xs font-semibold">Products to Dispatch</label>
                  <button type="button" onClick={addItem} className="text-xs bg-zinc-800 text-zinc-300 border border-zinc-700 px-2 py-1 rounded-lg"><Plus size={10} className="inline mr-1" />Add</button>
                </div>
                {form.items.map((it, i) => (
                  <div key={i} className="flex gap-2 mb-1.5">
                    <select value={it.product_id} onChange={e => updateItem(i, 'product_id', e.target.value)}
                      className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-50 text-xs">
                      <option value="">Product...</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.current_stock} {p.unit})</option>)}
                    </select>
                    <input type="number" min="0" step="any" placeholder="Qty" value={it.qty} onChange={e => updateItem(i, 'qty', e.target.value)}
                      className="w-20 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-50 text-xs font-mono" />
                    <select value={it.unit} onChange={e => updateItem(i, 'unit', e.target.value)}
                      className="w-16 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-50 text-xs">
                      {['kg','packs','pcs','boxes','bags'].map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    {form.items.length > 1 && <button type="button" onClick={() => removeItem(i)} className="text-red-400 text-xs px-1"><X size={12} /></button>}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Dispatch Date</label>
                  <input type="date" value={form.dispatch_date} onChange={e => setForm(f => ({ ...f, dispatch_date: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Mode</label>
                  <select value={form.dispatch_mode} onChange={e => setForm(f => ({ ...f, dispatch_mode: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm">
                    <option value="transport">Transport</option>
                    <option value="courier">Courier</option>
                    <option value="hand_delivery">Hand Delivery</option>
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Total Weight (kg)</label>
                  <input type="number" step="any" value={form.total_weight} onChange={e => setForm(f => ({ ...f, total_weight: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm font-mono" />
                </div>
              </div>

              {/* Transport details */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block flex items-center justify-between">
                    Transporter / Courier
                    <button type="button" onClick={() => setShowAddTransporter(true)} className="text-green-400 text-[10px] underline">+ New</button>
                  </label>
                  <select value={form.transporter_name} onChange={e => setForm(f => ({ ...f, transporter_name: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm">
                    <option value="">Select...</option>
                    {transporters.map(t => <option key={t.id} value={t.name}>{t.name} ({t.type})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Vehicle No</label>
                  <input value={form.vehicle_no} onChange={e => setForm(f => ({ ...f, vehicle_no: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm" placeholder="MH 12 AB 1234" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">LR / AWB No</label>
                  <input value={form.lr_no} onChange={e => setForm(f => ({ ...f, lr_no: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Tracking Link</label>
                  <input value={form.tracking_link} onChange={e => setForm(f => ({ ...f, tracking_link: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm" placeholder="https://..." />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Expected Delivery</label>
                  <input type="date" value={form.expected_delivery} onChange={e => setForm(f => ({ ...f, expected_delivery: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm" />
                </div>
              </div>

              {/* Invoice */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="flex items-center gap-2 text-zinc-300 text-xs">
                  <input type="checkbox" checked={form.gst_invoice} onChange={e => setForm(f => ({ ...f, gst_invoice: e.target.checked }))} className="rounded" />
                  GST Invoice
                </label>
                <div>
                  <input value={form.invoice_no} onChange={e => setForm(f => ({ ...f, invoice_no: e.target.value }))} placeholder="Invoice No"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs" />
                </div>
                <div>
                  <input value={form.eway_bill_no} onChange={e => setForm(f => ({ ...f, eway_bill_no: e.target.value }))} placeholder="E-Way Bill"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs" />
                </div>
              </div>

              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">Notes</label>
                <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm" />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-green-600 text-white font-semibold text-sm disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create Dispatch'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Transporter Mini Modal */}
      {showAddTransporter && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-sm p-4 space-y-3">
            <h4 className="text-zinc-50 text-sm font-semibold">Add Transporter / Courier</h4>
            <select value={newTransporter.type} onChange={e => setNewTransporter(p => ({ ...p, type: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-xs">
              <option value="transport">Transport</option>
              <option value="courier">Courier</option>
            </select>
            <input placeholder="Name" value={newTransporter.name} onChange={e => setNewTransporter(p => ({ ...p, name: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-xs" />
            <input placeholder="Phone" value={newTransporter.phone} onChange={e => setNewTransporter(p => ({ ...p, phone: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-xs" />
            <div className="flex gap-2">
              <button onClick={() => setShowAddTransporter(false)} className="flex-1 py-2 rounded-lg border border-zinc-700 text-zinc-400 text-xs">Cancel</button>
              <button onClick={addTransporter} className="flex-1 py-2 rounded-lg bg-green-600 text-white text-xs font-semibold">Add</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />
    </div>
  );
}

// ─── Dispatch Table Component ─────────────────────────────────────────────────
function DispatchTable({ dispatches, onStatusChange, onShare, expanded, onExpand, isAdmin, onDelete }) {
  const STATUSES = ['Planned', 'Packed', 'In Transit', 'Delivered', 'Received'];
  return (
    <div className="overflow-x-auto">
    <table className="w-full text-sm min-w-[900px]">
      <thead><tr className="border-b border-zinc-800">
        {['ID', 'Client', 'Items', 'Weight', 'Mode', 'Transport', 'Status', 'Tracking', 'Date', 'Actions'].map(h => (
          <th key={h} className="text-left text-zinc-500 text-xs font-medium px-3 py-2.5">{h}</th>
        ))}
      </tr></thead>
      <tbody>
        {dispatches.map(d => (
          <React.Fragment key={d.id}>
            <tr className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer" data-testid={`dispatch-row-${d.id}`}
              onClick={() => onExpand(expanded === d.id ? null : d.id)}>
              <td className="px-3 py-2 font-mono text-green-400 text-xs font-bold">{d.id}</td>
              <td className="px-3 py-2 text-zinc-200 text-xs">{d.client}</td>
              <td className="px-3 py-2 text-zinc-400 text-xs">
                {(d.items || []).slice(0, 3).map((it, i) => (
                  <span key={i} className="inline-flex items-center gap-1 mr-2">
                    <ProductCodeBadge name={it.product_name} code={it.product_code} />
                    <span>{it.product_name}</span>
                  </span>
                ))}
                {(d.items || []).length > 3 && <span className="text-zinc-600">+{d.items.length - 3}</span>}
              </td>
              <td className="px-3 py-2 font-mono text-zinc-300 text-xs">{d.total_weight} kg</td>
              <td className="px-3 py-2 text-xs"><span className={`px-1.5 py-0.5 rounded text-[10px] ${d.dispatch_mode === 'courier' ? 'bg-blue-900/40 text-blue-300' : 'bg-zinc-800 text-zinc-400'}`}>{d.dispatch_mode}</span></td>
              <td className="px-3 py-2 text-zinc-400 text-xs">{d.transporter_name || '-'}</td>
              <td className="px-3 py-2">
                <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[d.status] || ''}`}>{d.status}</span>
              </td>
              <td className="px-3 py-2">
                {d.tracking_link ? (
                  <div className="flex items-center gap-1">
                    <a href={d.tracking_link} target="_blank" rel="noopener noreferrer" className="text-green-400 text-[10px] underline" onClick={e => e.stopPropagation()}>Track</a>
                    {!d.tracking_shared_with_client && (
                      <button onClick={e => { e.stopPropagation(); onShare(d.id); }} className="text-[10px] text-amber-400 underline">Share</button>
                    )}
                    {d.tracking_shared_with_client && <CheckCircle2 size={10} className="text-green-400" />}
                  </div>
                ) : <span className="text-zinc-600 text-xs">-</span>}
              </td>
              <td className="px-3 py-2 font-mono text-zinc-500 text-xs">{d.dispatch_date}</td>
              <td className="px-3 py-2">
                {d.status !== 'Received' && (
                  <select value={d.status} onClick={e => e.stopPropagation()}
                    onChange={e => { e.stopPropagation(); onStatusChange(d.id, e.target.value); }}
                    className={`text-xs px-2 py-1 rounded-lg border bg-transparent cursor-pointer ${STATUS_STYLES[d.status] || ''}`}>
                    {STATUSES.map(s => <option key={s} value={s} className="bg-zinc-900 text-zinc-50">{s}</option>)}
                  </select>
                )}
                {d.status === 'Received' && <span className="text-green-400 text-xs flex items-center gap-1"><CheckCircle2 size={12} /></span>}
                {isAdmin && <button onClick={e => { e.stopPropagation(); onDelete(d.id, d.client); }} className="p-1 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30 ml-1"><Trash2 size={12} /></button>}
              </td>
            </tr>
            {expanded === d.id && (
              <tr><td colSpan={10} className="p-0">
                <div className="bg-zinc-950/60 border-b border-zinc-800 px-4 py-3 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 text-xs">
                  <div><span className="text-zinc-500">Vehicle:</span> <span className="text-zinc-300 font-mono">{d.vehicle_no || '-'}</span></div>
                  <div><span className="text-zinc-500">LR No:</span> <span className="text-zinc-300 font-mono">{d.lr_no || '-'}</span></div>
                  <div><span className="text-zinc-500">E-Way:</span> <span className="text-zinc-300 font-mono">{d.eway_bill_no || '-'}</span></div>
                  <div><span className="text-zinc-500">Invoice:</span> <span className="text-zinc-300">{d.gst_invoice ? 'GST' : 'Non-GST'} {d.invoice_no}</span></div>
                  <div><span className="text-zinc-500">Expected:</span> <span className="text-zinc-300 font-mono">{d.expected_delivery || '-'}</span></div>
                  <div><span className="text-zinc-500">Delivered:</span> <span className="text-zinc-300 font-mono">{d.actual_delivery || '-'}</span></div>
                  <div><span className="text-zinc-500">Condition:</span> <span className="text-zinc-300">{d.received_condition || '-'}</span></div>
                  <div><span className="text-zinc-500">Sales Order:</span> <span className="text-zinc-300 font-mono">{d.sales_order_id || '-'}</span></div>
                  <div><span className="text-zinc-500">Notes:</span> <span className="text-zinc-300">{d.notes || '-'}</span></div>
                </div>
              </td></tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
    </div>
  );
}
